import {
  portalFileFormats,
  portalMaxFileBytes,
  type PortalFileFormat,
} from "@ledgerbox/domain"

/** One row of the help sheet: what happens, in the client's words. */
export type HelpRow = { title: string; body: string }

/** The home view the client sees: the period checklist, or the engagement
 * task list that has its own send button. */
export type HelpMode = "checklist" | "engagement"

/* Each format the upload takes, as a client would name it. A format added to
 * the shared policy without a kind here fails the typecheck. */
const FORMAT_KIND = {
  pdf: "PDFs",
  jpg: "photos",
  png: "photos",
  heic: "photos",
  heif: "photos",
  csv: "spreadsheets",
  xlsx: "spreadsheets",
  docx: "Word files",
  txt: "text files",
  ofx: "bank downloads",
  qfx: "bank downloads",
  qif: "bank downloads",
  qbo: "bank downloads",
} satisfies Record<PortalFileFormat["extension"], string>

/** The formats a client can send, as kinds: "PDFs, photos, … and bank
 * downloads", in policy order and read from the shared file policy. */
export const acceptedFileKinds = (): string => {
  const kinds = [
    ...new Set(
      portalFileFormats.map((format) => FORMAT_KIND[format.extension])
    ),
  ]
  const last = kinds.at(-1)
  return kinds.length > 1
    ? `${kinds.slice(0, -1).join(", ")} and ${last}`
    : (last ?? "")
}

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1)

/** The per-file cap in whole megabytes, from the same policy. */
export const maxFileMegabytes = (): number =>
  Math.round(portalMaxFileBytes / (1024 * 1024))

/**
 * The five things a first-time client needs to know, in the order they
 * happen. Every claim here is one the portal code keeps: the done button's
 * real label, the file types and size the upload accepts, and where replies
 * and shared files show up.
 */
export const portalHelpRows = ({
  firmName,
  sectionLabel,
  mode,
}: {
  firmName: string
  sectionLabel: string
  mode: HelpMode
}): HelpRow[] => {
  const doneLabel = "I've sent everything"
  return [
    {
      title: `${firmName} asks for documents`,
      body:
        mode === "engagement"
          ? `The ${sectionLabel} tab lists what ${firmName} needs from you. Open each task to see what to send.`
          : `The ${sectionLabel} tab lists what ${firmName} needs from you. Each item says what to send, and some ask a short question instead of a file.`,
    },
    {
      title: "Add your files",
      body: `Choose a file for each ${mode === "engagement" ? "task" : "item"}, or take a photo on your phone. Send what you have now and the rest later. Each file can be up to ${maxFileMegabytes()} MB. ${capitalize(acceptedFileKinds())} all work.`,
    },
    {
      title: `Tell ${firmName} you sent everything`,
      body:
        mode === "engagement"
          ? `The ${doneLabel} button turns on once every required task is in. ${firmName} then checks what you sent before work begins.`
          : `The ${doneLabel} button turns on once everything required is in. ${firmName} then checks each file. If they need one again, it opens on your list and they ask for it in Messages.`,
    },
    {
      title: "Ask a question",
      body: `Use Messages to ask ${firmName} about your list or a file you sent. Their reply shows up there.`,
    },
    {
      title: "Get files back",
      body: `Reports and files ${firmName} shares with you show up under For you`,
    },
  ]
}
